<template>
  <div class="pa-4">
    <div v-if="loading && !stripeReady" class="text-center">
      <h3 class="gray--text">{{ $t('message.checking_connection') }}...</h3>
    </div>
    <div v-else>
      <div>
        <v-alert v-if="!loading && paymentsReady && !stripeReady && paymentsModule != 'easypay'" elevation="0"
          color="warning" dense class="text-center">
          <v-icon size="20">mdi-information-outline</v-icon>
          <span @click="connectPaymentDialog = true" v-html="$t('message.connect_payments_explained')"></span>
        </v-alert>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-spacer></v-spacer>
          <v-btn outlined elevation="0" small color="primary" @click="sellItem()">
            <v-icon class="mr-1">mdi-cart-outline</v-icon>
            <span>{{ $t("message.sell_item") }}</span>
          </v-btn>
          <v-btn outlined elevation="0" small color="primary" @click="goToNewEvent()">
            <v-icon class="mr-1">mdi-calendar</v-icon>
            <span>{{ $t("message.create_event") }}</span>
          </v-btn>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-if="stripeReady" class="ml-1" v-bind="attrs" v-on="on" elevation="0" small color="secondary"
                @click="goToStripeSupport()">
                <v-icon class="mr-1">mdi-lifebuoy</v-icon>
                <span>{{ $t("message.stripe_support") }}</span>
              </v-btn>
            </template>
            <span>{{ $t('message.transaction_problems') }}</span>
          </v-tooltip>
          <v-btn class="ml-1" v-if="!isSmallerDisplay()" small :disabled="downloadingAllTransactions" color="secondary"
            elevation="0" @click="detailedReportDialog = true">
            <v-progress-circular small indeterminate color="white" v-if="downloadingAllTransactions">
            </v-progress-circular>
            <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
            {{ $t('message.report') }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn outlined color="primary" elevation="0" v-on="on" block small slot="activator" left>
                {{ $t('message.actions') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click.stop="sellItem()">
                <v-list-item-title>
                  <v-icon class="mr-1">mdi-cart-outline</v-icon>
                  <span>{{ $t("message.sell_item") }}</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click.stop="goToNewEvent()">
                <v-list-item-title>
                  <v-icon class="mr-1">mdi-calendar</v-icon>
                  <span>{{ $t("message.create_event") }}</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click.stop="goToStripeSupport()">
                <v-list-item-title>
                  <v-icon class="mr-1">mdi-lifebuoy</v-icon>
                  <span>{{ $t("message.stripe_support") }}</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click.stop="detailedReportDialog = true">
                <v-list-item-title>
                  <v-progress-circular small indeterminate color="white" v-if="downloadingAllTransactions">
                  </v-progress-circular>
                  <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                  {{ $t('message.report') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
        <v-tabs show-arrows v-model="activeTab" slider-color="tertiary" class="mt-4">
          <v-tab :key="'dashboard'" @click="clickTab(0)" ripple>
            {{ $t('message.quick_look') }}</v-tab>
          <v-tab @click="clickTab(1)" :key="'subscriptions'" ripple>
            {{ $t('message.memberships') }}</v-tab>
          <v-tab :key="'onetimepurchases'" @click="clickTab(2)" ripple>{{ $t('message.one_time_purchases') }}</v-tab>
          <v-tab v-show="stripeReady || (paymentsModule == 'easypay' && paymentsReady)" :key="'invoices'"
            @click="clickTab(3)" ripple>
            {{ $t('message.credit_card_invoices') }}</v-tab>
          <v-tab :key="'expenses'" @click="clickTab(4)" ripple>{{ $t('message.expenses') }}</v-tab>
          <v-tab-item :key="'aggregates'">
            <v-card outlined elevation="0" class="pa-0 mt-6">
              <div v-allowed="'view_income'">
                <GraphLoader v-if="loadingCurrentMonthIncome">
                </GraphLoader>
                <div v-if="chartDataIncomeMonthly && !loadingCurrentMonthIncome" class="text-center">
                  <v-row align="center">
                    <v-col cols="12" md="3">
                      <v-card elevation="0">
                        <v-card-title class="pl-2 text-subtitle-2 text-center">{{ $t('message.current_month')
                        }}
                          <v-spacer></v-spacer>
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn @click="triggerStatsRefresh(true)" :loading="triggeringRefresh" elevation="0" icon
                                v-bind="attrs" v-on="on"><v-icon>mdi-sync</v-icon></v-btn>
                            </template>
                            <span>{{ $t('message.refresh_data') }}</span>
                          </v-tooltip>
                        </v-card-title>
                        <v-card-text class="mb-0 pb-0">
                          <div class="text-h3">
                            <div v-if="currentMonthTotal > 0" class="ml-2 success--text">
                              <b>{{ currencySymbol }}{{
                                currentMonthTotal.toFixed(0)
                              }}</b>
                            </div>
                            <div v-else-if="currentMonthTotal < 0" class="ml-2 error--text">
                              <b>
                                -{{ currencySymbol }}{{ Math.abs(currentMonthTotal).toFixed(0) }}
                              </b>
                            </div>
                          </div>
                          <div v-if="payoutsHistory && !isNaN(payoutsHistory.future)">
                            <span class="text-subtitle-2 mr-1 text-caption">
                              <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
                              {{ $t('message.future_payouts') }}: </span>
                            <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.future).toFixed(2) }}</span>
                          </div>
                        </v-card-text>
                        <v-card-actions v-if="!isSmallerDisplay()">
                          <div v-if="chartOptionsIncomeMonthly.lastUpdate" class="mt-2 text-center text-caption">{{
                            $t('message.last_update')
                            }} {{ $d(new
                              Date(chartOptionsIncomeMonthly.lastUpdate), 'long',
                              $i18n.locale) }}</div>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="9">
                      <ApexChart type="bar" :options="chartOptionsIncomeMonthly" height="160" width="100%"
                        :series="chartDataIncomeMonthly">
                      </ApexChart>
                      <v-card-actions v-if="isSmallerDisplay()">
                        <div v-if="chartOptionsIncomeMonthly.lastUpdate" class="mt-2 text-center text-caption">{{
                          $t('message.last_update')
                        }} {{ $d(new
                            Date(chartOptionsIncomeMonthly.lastUpdate), 'long',
                            $i18n.locale) }}</div>
                      </v-card-actions>
                    </v-col>
                  </v-row>
                </div>
                <v-card-text v-else-if="!loadingCurrentMonthIncome" class="text-center text-subtitle-1">
                  {{ $t('message.current_month_processing_come_back') }}
                </v-card-text>
              </div>
            </v-card>
            <v-card outlined elevation="0" class="pa-0 mt-6">
              <div class="pa-2">
                <v-select @change="filterDatedIncome" outlined dense v-model="selectedIncomeGraphMonths"
                  :items="availableMonths" hide-details :label="$t('message.months')"></v-select>
              </div>
              <v-divider class="mb-2"></v-divider>
              <GraphLoader v-if="loadingBottomCharts">
              </GraphLoader>
              <div v-else v-allowed="'view_income'" class="mt-2">
                <v-card-actions class="mt-0 pt-0">
                  <v-switch @change="buildIncomeBars()" :label="$t('message.by_quarter')"
                    v-model="incomeBarQuarterly"></v-switch>
                  <v-spacer></v-spacer>
                  <v-tooltip top v-if="chartOptionsIncomeMonthly.lastUpdate">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="25">mdi-information-outline</v-icon>
                    </template>
                    <span>
                      {{
                        $t('message.last_update')
                      }} {{ $d(new
                        Date(chartOptionsIncomeMonthly.lastUpdate), 'long',
                        $i18n.locale) }}</span>
                  </v-tooltip>
                </v-card-actions>
                <div v-if="incomeBars" class="text-center">
                  <ApexChart style="margin-top:-30px" type="line" :options="incomeBars.options" height="200"
                    :series="incomeBars.data">
                  </ApexChart>
                </div>
              </div>
              <div v-if="productsGraph.ready" elevation="0" class="pa-0 mt-6" v-allowed="'view_income'">
                <div class="text-subtitle-2 text-center">{{ $t('message.top_products') }}</div>
                <ApexChart type="pie" :options="productsGraph.thisYear.options" height="350"
                  :series="productsGraph.thisYear.data">
                </ApexChart>
                <v-card flat outlined v-if="pieChartSelection" class="ml-2 mr-2 mb-2">
                  <v-card-actions class="mb-0 mt-0 pb-0 pt-0">
                    <v-spacer></v-spacer>
                    <v-btn small icon
                      @click="pieChartSelection = null"><v-icon>mdi-close-circle-outline</v-icon></v-btn>
                  </v-card-actions>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>{{ pieChartSelection.label }}</v-list-item-title>
                      <v-list-item-subtitle>{{ pieChartSelection.count }} {{ $t('message.top_products') }} ({{
                        pieChartSelection.revenue }})</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card>
              </div>
            </v-card>
            <v-card outlined elevation="0" class="pa-0 mt-6">
              <div v-allowed="'view_income'">
                <GraphLoader type="line" v-if="loadingIncomeTrendline">
                </GraphLoader>
                <div v-else-if="incomeTrendline" class="text-center">
                  <span class="pl-2 text-subtitle-2">{{ $t('message.last_12_months') }}</span>
                  <ApexChart style="margin-top:-30px" type="line" :options="incomeTrendline.options" height="200"
                    :series="incomeTrendline.data">
                  </ApexChart>
                </div>
              </div>
            </v-card>
            <v-card v-allowed="'view_income'" v-if="paymentsReady" outlined elevation="0" class="pa-0 mt-2">
              <v-data-table v-if="!isSmallerDisplay() && stripeReady" :headers="payoutsHistory.headers"
                :items="payoutsHistory.data" :items-per-page="5" class="elevation-0" :loading="payoutsHistory.loading">
                <template v-slot:top>
                  <div class="pa-2">
                    <v-row
                      v-if="!payoutsHistory.loading && payoutsHistory && payoutsHistory.data && payoutsHistory.data.length">
                      <v-col v-if="payoutsHistory.connectReserved > 0">
                        <span class="text-subtitle-2 mr-1">
                          <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
                          {{ $t('message.pending_confirmation') }}: </span>
                        <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.connectReserved).toFixed(2)
                        }}</span>
                      </v-col>
                      <v-col>
                        <span class="text-subtitle-2 mr-1">
                          <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
                          {{ $t('message.future_payouts') }}: </span>
                        <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.future).toFixed(2) }}</span>
                      </v-col>
                      <v-col>
                        <span class="text-subtitle-2 mr-1">
                          <v-icon class="mr-1">mdi-truck-fast-outline</v-icon>
                          {{ $t('message.in_transit') }}: </span>
                        <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.inTransit).toFixed(2) }}</span>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <span class="text-subtitle-1 mr-1">
                          {{ $t('message.payouts') }}: </span>
                      </v-col>
                    </v-row>
                  </div>
                </template>
                <template v-slot:item.started="{ item }">
                  {{ $d(new Date(item.started), "short", $i18n.locale) }}
                </template>
                <template v-slot:item.arrival="{ item }">
                  {{ $d(new Date(item.arrival), "short", $i18n.locale) }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="getPayoutStatusColor(item.status)">
                    {{ $t('message.' + item.status).toLowerCase() }}
                  </v-chip>
                </template>
                <template v-slot:item.amount="{ item }">
                  {{ currencySymbol }}{{ item.amount ? item.amount.toFixed(2) : 0 }}
                </template>
              </v-data-table>
              <div v-else-if="payoutsHistory.data && payoutsHistory.data.length && stripeReady">
                <div class="pa-2">
                  <v-row v-if="payoutsHistory.connectReserved">
                    <v-col>
                      <span class="text-subtitle-2 mr-1">
                        <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
                        {{ $t('message.pending_confirmation') }}: </span>
                      <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.connectReserved).toFixed(2)
                      }}</span>
                    </v-col>
                  </v-row>
                  <v-row v-if="!payoutsHistory.loading">
                    <v-col>
                      <span class="text-subtitle-2 mr-1">
                        <v-icon class="mr-1">mdi-receipt-clock-outline</v-icon>
                        {{ $t('message.future_payouts') }}: </span>
                      <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.future).toFixed(2) }}</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <span class="text-subtitle-2 mr-1">
                        <v-icon class="mr-1">mdi-truck-fast-outline</v-icon>
                        {{ $t('message.in_transit') }}: </span>
                      <span>{{ currencySymbol }}{{ Number.parseFloat(payoutsHistory.inTransit).toFixed(2) }}</span>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <span class="text-subtitle-1 mr-1">
                        {{ $t('message.payouts') }}: </span>
                    </v-col>
                  </v-row>
                </div>
                <v-simple-table fixed-header height="300px">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t("message.arriving") }}
                        </th>
                        <th class="text-left">
                          {{ $t("message.amount") }}
                        </th>
                        <th class="text-left">
                          {{ $t("message.status") }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in payoutsHistory.data" :key="item.id">
                        <td>{{ $d(new Date(item.arrival), "short", $i18n.locale) }}</td>
                        <td>
                          {{ currencySymbol }}{{ item.amount }}
                        </td>
                        <td>
                          <v-chip small :color="getPayoutStatusColor(item.status)">
                            {{ $t('message.' + item.status).toLowerCase() }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'subscriptions'" v-allowed="'view_subscriptions'">
            <div v-if="loading && stripeReady" style="margin-top:10px" class="text-center">
              <v-skeleton-loader type="table">
              </v-skeleton-loader>
            </div>
            <v-container fluid v-if="!loading">
              <v-select outlined dense v-model="subscriptionsFilter" :items="subscriptionsFilters" hide-details
                item-text="text" item-value="value" :label="$t('message.filter_memberships')" clearable></v-select>
            </v-container>
            <v-container v-if="!loading">
              <v-row v-allowed="'view_income'">
                <v-col cols="12" sm="6">
                  <v-card elevation="0" outlined class="text-center">
                    <v-card-title class="text-subtitle-2 gray--text">{{ totalCountTitle }}</v-card-title>
                    <v-card-text class="text-h3">{{ subscriptionsFiltered ? subscriptionsFiltered.length :
                      0 }}</v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-card v-bind="attrs" v-on="on" elevation="0" outlined class="text-center">
                        <v-card-title class="text-subtitle-2 gray--text">
                          {{ totalAmountTitle }}
                          <v-icon class="ml-1" size="15" style="margin-top:-5px">mdi-information-outline</v-icon>
                        </v-card-title>
                        <v-card-text class="text-h3">{{ currencySymbol }}{{ totalRecurrentIncome }}</v-card-text>
                      </v-card>
                    </template>
                    <span>{{ $t('message.calculated_monthly_basis') }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
            <v-card-text class="mb-0 pb-0" v-if="!loading">
              <v-row>
                <v-col class="pb-0" cols="12" sm="12" md="6">
                  <v-text-field hide-details @keyup="filterSubscriptions" :label="$t('message.filter_by_member')"
                    outlined dense v-model="subscriptionUserFilter"></v-text-field>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <v-autocomplete hide-details @change="filterSubscriptions" outlined dense
                    v-model="subscriptionProductFilter" :items="membershipProducts"
                    :label="$t('message.filter_by_membership')" item-text="text" item-value="value" clearable>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pb-0" cols="12" sm="12">
                  <tags-select v-if="availableTags" v-model="subscriptionTagFilter" @change="filterSubscriptions"
                    :label="$t('message.filter_table_label')" :items="availableTags"></tags-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="12" md="5">
                  <div class="text-subtitle-2">
                    {{ $t('message.from') }}:
                  </div>
                  <div>
                    <DatePicker @change="getRecurrentTransactions(true)" :show-time="false" v-model="fromFilter"
                      :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
                <v-col cols="12" sm="12" md="5">
                  <div class="text-subtitle-2">
                    {{ $t('message.to') }}:
                  </div>
                  <div>
                    <DatePicker @change="getRecurrentTransactions(true)" :show-time="false" v-model="toFilter"
                      :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
                <v-col cols="12" sm="12" md="2">
                  <v-btn small elevation="0" @click="getRecurrentTransactions()">
                    {{ $t('message.clear') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
            <div style="margin-top:10px" v-if="!loading && subscriptions && subscriptions.length > 0">
              <v-card-actions class="mr-2">
                <v-spacer></v-spacer>
                <v-btn v-if="!isSmallerDisplay()" outlined :disabled="downloadingMembershipsTable"
                  :loading="downloadingMembershipsTable" text elevation="0" color="primary" small
                  @click="downloadMembershipsTable()">
                  <v-icon class="mr-1">mdi-table-arrow-down</v-icon>
                  <span>{{ $t('message.download_table') }}</span>
                </v-btn>
                <v-btn outlined v-if="membershipsTrendline.ready" text elevation="0" color="primary" small
                  @click="membershipsTrendlineToggle = !membershipsTrendlineToggle">
                  <v-icon class="mr-1" v-if="membershipsTrendlineToggle">mdi-table</v-icon>
                  <v-icon class="mr-1" v-else>mdi-chart-line</v-icon>
                  <span v-if="membershipsTrendlineToggle && !isSmallerDisplay()">{{ $t('message.show_table') }}</span>
                  <span v-else-if="!isSmallerDisplay()">{{ $t('message.show_graph') }}</span>
                </v-btn>
              </v-card-actions>
              <div v-show="membershipsTrendlineToggle">
                <ApexChart type="area" :options="membershipsTrendline.options" height="350"
                  :series="membershipsTrendline.data">
                </ApexChart>
              </div>
              <div v-if="isSmallerDisplay()">
                <v-list class="text-left" dense v-if="!membershipsTrendlineToggle">
                  <v-subheader>
                    <v-icon size="20" class="mr-1">mdi-credit-card-outline</v-icon>
                    {{ $t('message.active') }} {{ $t('message.subscriptions') }} ({{ subscriptionsFiltered.length }})
                  </v-subheader>
                  <template v-for="(i, index) in subscriptionsFiltered">
                    <!--Add third row-->
                    <v-list-item :key="'subscription' + index" class="text-left"
                      @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } })">
                      <v-list-item-avatar>
                        <member-avatar :status="i.lastPaymentStatus"
                          :image="i.buyer && i.buyer.userImageSmall ? i.buyer.userImageSmall : null" size="35">
                        </member-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="font-weight-bold">
                            {{ i.buyer.displayName }}
                          </span>
                          <span class="gray--text">
                            <v-icon size="20" color="accent"
                              v-if="i.coupon && i.coupon.discountedTotal > 0">mdi-tag-outline</v-icon>
                            <span class="ml-1 mr-1">●</span>{{ currencySymbol }}{{ getTransactionTotal(i) }}
                          </span>
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="i.time && i.buyer">
                          {{ i.product.name }} -
                          {{ $d(new Date(i.time), 'long', $i18n.locale) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <span v-if="i.lastPaymentStatus == 'paused'">
                            {{ $t('message.paused_until') }}:
                            {{ $d(new Date(i.pausedUntil), 'month_day_year', $i18n.locale) }}
                          </span>
                          <span v-else-if="i.lastPaymentStatus == 'pending_payment'"></span>
                          <span v-else-if="i.lastPaymentStatus == 'trialing'">
                            {{ $t('message.start_date') }}:
                            {{ $d(new Date(i.subscriptionStartDate), 'month_day_year', $i18n.locale) }}
                          </span>
                          <span v-else-if="i.expiringSoon">
                            <v-icon color="error" class="mr-1">mdi-clock-alert-outline</v-icon>
                            {{ $t('message.expires') }}:
                            {{ i.subscriptionEndDate ? $d(new Date(i.subscriptionEndDate), 'month_day_year',
                              $i18n.locale)
                              : i.subscriptionCheckins + ' ' + $t('message.checkins') }}
                          </span>
                          <span v-else-if="i.pauseDate">
                            <v-icon :color="getTransactionStatusColor('paused')"
                              class="mr-1">mdi-timer-pause-outline</v-icon>
                            {{ $d(new Date(i.pauseDate), 'month_day_year',
                              $i18n.locale) }}
                          </span>
                          <span v-else-if="i.cancellationDate">
                            <v-icon color="error" class="mr-1">mdi-timer-cancel-outline</v-icon>
                            {{ $d(new Date(i.cancellationDate), 'month_day_year',
                              $i18n.locale) }}
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="index + 1 < subscriptionsFiltered.length" :key="index"></v-divider>
                  </template>
                </v-list>
              </div>
              <div v-else>
                <v-data-table :loading="loadingMembershipsTable" v-show="!membershipsTrendlineToggle" item-key="_id"
                  :headers="subscriptionsHeaders" :items="subscriptionsFiltered" :custom-sort="subscriptionsSort"
                  class="elevation-0" :no-data-text="$t('message.no_data')" :footer-props="{
                    itemsPerPageText: $t('message.rows_per_page'),
                    itemsPerPageOptions: [25, 50, 75],
                  }">
                  <template v-slot:item.buyer="{ item }">
                    <div class="d-flex align-center" style="gap: 8px">
                      <member-avatar :image="item.buyer && item.buyer.userImageSmall ? item.buyer.userImageSmall : null"
                        size="30">
                      </member-avatar>
                      <span v-if="item.buyer" class="clickable"
                        @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: item._id } })">
                        {{ item.buyer.displayName }}
                      </span>
                      <span v-else>
                        {{ $t('message.pending_onboarding') }}
                      </span>
                    </div>
                  </template>
                  <template v-slot:item.productName="{ item }">
                    <span class="clickable"
                      @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: item._id } })">
                      <v-tooltip top v-if="item.hasNotes">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" size="15" color="accent">mdi-message-text-outline</v-icon>
                        </template>
                        <span>{{ $t('message.has_notes') }}</span>
                      </v-tooltip>
                      {{ truncate(item.productName, 30, false) }}
                    </span>
                  </template>
                  <template v-slot:item.productInterval="{ item }">
                    <span class="clickable" v-if="item.productInterval == 'month'">
                      {{ $t('message.interval_month').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'day'">
                      {{ $t('message.interval_day').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'week'">
                      {{ $t('message.interval_week').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'bi-week'">
                      {{ $t('message.interval_bi-week').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'bi-year'">
                      {{ $t('message.interval_bi-year').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'year'">
                      {{ $t('message.interval_year').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else-if="item.productInterval == 'once'">
                      {{ $t('message.once').toLowerCase() }}
                    </span>
                    <span class="clickable" v-else>
                      <div class="warning--text" v-if="item.expiringSoon && item.lastPaymentStatus !== 'expired'">
                        <v-icon color="warning" small class="mr-1">mdi-clock-alert-outline</v-icon>
                        {{ item.subscriptionEndDate
                          ? Math.ceil((new Date(item.subscriptionEndDate).getTime() - Date.now()) / 86400000) + ' days left'
                          : item.subscriptionCheckins + ' ' + $t('message.checkins') }}
                      </div>
                      <div v-else>
                        {{ $t('message.manual').toLowerCase() }}
                        <v-tooltip top v-if="item.manualRecurrence">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon size="25" color="accent" v-bind="attrs" v-on="on">mdi-account-sync-outline</v-icon>
                          </template>
                          <span> {{ $t('message.keep_membership_visible') }}</span>
                        </v-tooltip>
                      </div>
                    </span>
                  </template>
                  <template v-slot:item.lastPaymentStatus="{ item }">
                    <v-badge v-if="item.paymentType == 'bank' && item.paymentMethod" bordered color="accent"
                      icon="mdi-shield-check" overlap>
                      <v-chip
                        :color="item.lastPaymentStatus ? getTransactionStatusColor(item.lastPaymentStatus) : 'success'"
                        small class="white--text">
                        <v-icon size="15" color="white" class="mr-1">mdi-bank</v-icon>
                        {{ item.lastPaymentStatus ? getTransactionStatusText(item.lastPaymentStatus).toLowerCase() :
                          $t("message.completed").toLowerCase() }}
                      </v-chip>
                    </v-badge>
                    <v-chip v-else
                      :color="item.lastPaymentStatus ? getTransactionStatusColor(item.lastPaymentStatus, item) : 'success'"
                      small class="white--text"
                      :style="(item.cancellationDate || item.pauseDate) ? 'height:auto;padding:4px 8px;' : ''">
                      <span v-if="item.lastPaymentStatus == 'pending_payment' && item.paymentTypes">
                        <v-icon v-if="item.paymentTypes.length > 1" size="20" color="white"
                          class="mr-1">mdi-card-multiple-outline</v-icon>
                        <v-icon v-else-if="item.paymentTypes.length == 1 && item.paymentTypes[0].indexOf('bank') >= 0"
                          size="20" color="white" class="mr-1">mdi-bank</v-icon>
                        <v-icon v-else size="20" color="white" class="mr-1">mdi-credit-card-outline</v-icon>
                      </span>
                      <v-icon size="20" color="white" class="mr-1"
                        v-else-if="item.cancellationDate">mdi-timer-cancel-outline</v-icon>
                      <v-icon size="20" color="white" class="mr-1"
                        v-else-if="item.pauseDate">mdi-timer-pause-outline</v-icon>
                      <v-icon size="20" color="white" class="mr-1"
                        v-else-if="item.paymentType == 'bank'">mdi-bank</v-icon>
                      <v-icon size="20" color="white" class="mr-1" v-else-if="item.paidWithCash">mdi-cash-100
                      </v-icon>
                      <v-icon size="20" color="white" class="mr-1" v-else>mdi-credit-card-outline</v-icon>
                      <span v-if="item.cancellationDate" class="d-flex flex-column" style="line-height:1.3;">
                        <span>{{ $t("message.scheduled_cancellation").toLowerCase() }}</span>
                        <span class="text-caption">{{ $d(new Date(item.cancellationDate), 'month_day_year', $i18n.locale) }}</span>
                      </span>
                      <span v-else-if="item.pauseDate" class="d-flex flex-column" style="line-height:1.3;">
                        <span>{{ $t("message.scheduled_pause").toLowerCase() }}</span>
                        <span class="text-caption">{{ $d(new Date(item.pauseDate), 'month_day_year', $i18n.locale) }}</span>
                      </span>
                      <span v-else-if="item.lastPaymentStatus">{{
                        getTransactionStatusText(item.lastPaymentStatus).toLowerCase() }}
                      </span>
                      <span v-else>
                        $t("message.completed").toLowerCase()
                      </span>
                    </v-chip>
                  </template>
                  <template v-slot:item.displayDate="{ item }">
                    {{ item.displayDate }}
                  </template>
                  <template v-slot:item.time="{ item }">
                    {{ $d(new Date(item.time), 'month_day_year', $i18n.locale) }}
                  </template>
                  <template v-slot:item.amount="{ item }">
                    <v-icon size="15" color="accent"
                      v-if="item.coupon && item.coupon.discountedTotal >= 0">mdi-tag-outline</v-icon>
                    {{ currencySymbol }}{{ getTransactionTotal(item) }}
                    <v-icon size="20" color="accent" class="ml-1"
                      v-if="item.receipt">mdi-receipt-text-check-outline</v-icon>
                  </template>
                </v-data-table>
              </div>
            </div>
            <div v-else-if="!loading" class="mt-2 text-center">
              <h3>{{ $t('message.no_subscriptions') }}</h3>
            </div>
            <v-expansion-panels class="mt-2" flat>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
                <v-expansion-panel-header>
                  <v-subheader @click="getCancelledSubcriptions()">
                    <span class="mr-2">
                      <v-icon size="24" class="mr-1">mdi-credit-card-outline</v-icon>
                    </span>

                    {{ $t('message.inactive') }} {{ $t('message.subscriptions') }}
                    <span v-if="cancelledFiltered.length > 0">({{ cancelledFiltered.length }})</span>
                  </v-subheader>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-skeleton-loader v-if="loadingCancelledSubscriptions" type="list-item-avatar-three-line">
                  </v-skeleton-loader>
                  <v-list class="text-left" dense>
                    <template v-for="(i, index) in cancelledFiltered">
                      <v-list-item :key="'cancelled' + index"
                        @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } })">
                        <v-list-item-avatar>
                          <v-avatar size="45">
                            <img v-if="i.buyer && i.buyer.userImageSmall" :src="userImg(i.buyer.userImageSmall)" />
                            <img v-else :src="assets('default-profile-avatar.png')" />
                          </v-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>
                            <h3>
                              {{ i.product ? i.product.name : 'Unknown' }}
                              <span class="ml-1 mr-1">●</span>
                              <span>(<v-icon size="15" color="accent"
                                  v-if="i.coupon && i.coupon.discountedTotal > 0">mdi-tag-outline
                                </v-icon>{{ currencySymbol + getTransactionTotal(i) }})</span>
                            </h3>
                          </v-list-item-title>
                          <v-list-item-title v-if="i.time && i.buyer">
                            {{ i.buyer.displayName }} -
                            {{ $d(new Date(i.time), 'long', $i18n.locale) }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider v-if="index + 1 < cancelledFiltered.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>
          <v-tab-item :key="'onetimepurchases'" v-allowed="'view_purchases'">
            <v-card elevation="0">
              <v-row class="pb-0 mt-2 mb-0">
                <v-col class="pb-0" cols="12" sm="12" md="6">
                  <v-autocomplete hide-details outlined dense v-model="userFilter" :items="members"
                    :label="$t('message.filter_by_member')" item-text="text" item-value="value" clearable>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <v-autocomplete hide-details outlined dense v-model="productFilter" :items="products"
                    :label="$t('message.filter_by_product')" item-text="text" item-value="value" clearable>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row class="pt-0 mt-0" style="margin-top:-20px">
                <v-col class="pt-0">
                  <v-card-actions>
                    <v-switch hide-details v-if="!productsResultByGraph" v-model="pendingFilter"
                      :label="$t('message.pending_payment')">
                    </v-switch>
                    <v-spacer></v-spacer>
                    <v-btn v-if="productFilter" outlined text elevation="0" color="primary" small
                      @click="productsResultByGraph = !productsResultByGraph; getProductTimline()">
                      <v-icon class="mr-1" v-if="!productsResultByGraph">mdi-chart-line</v-icon>
                      <v-icon class="mr-1" v-else>mdi-table</v-icon>
                      <span v-if="!productsResultByGraph">{{ $t('message.graph') }}</span>
                      <span v-else>{{ $t('message.table') }}</span>
                    </v-btn>
                  </v-card-actions>
                </v-col>
              </v-row>
              <div v-if="loadingOneTimeTransactions">
                <v-skeleton-loader
                  type="list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line">
                </v-skeleton-loader>
              </div>
              <div v-if="!loadingOneTimeTransactions && transactions && transactions.length > 0">
                <div v-if="productFilter && productsResultByGraph">
                  <v-skeleton-loader v-if="loadingProductTimeline" type="image">
                  </v-skeleton-loader>
                  <div v-else-if="productTimelineGraph && productTimelineGraph.options" class="text-center">
                    <ApexChart style="margin-top:-30px" type="bar" :options="productTimelineGraph.options" height="200"
                      :series="productTimelineGraph.data">
                    </ApexChart>
                    <div class="text-caption">
                      {{ $t('message.product_timeline_explained') }}
                    </div>
                  </div>
                  <v-card-text v-else class="text-center">
                    {{ $t('message.no_data') }}
                  </v-card-text>
                </div>
                <div v-else>
                  <v-list dense v-if="isSmallerDisplay()" class="text-left">
                    <template v-for="(i, index) in transactionsFiltered">
                      <v-list-item :key="'transaction' + index" @click.stop="goToTransaction(i)" class="pl-0 pr-0">
                        <v-badge :color="getStatusColor(i)"
                          :content="i.completed ? '✓' : (i.expired ? '⚠' : (i.cancelled ? 'x' : '∙∙∙'))" offset-x="30"
                          offset-y="28" overlap>
                          <v-list-item-action>
                            <TransactionIcon :transaction="i"></TransactionIcon>
                          </v-list-item-action>
                        </v-badge>
                        <v-list-item-avatar>
                          <img v-if="i.product && i.product.image" :src="userImg(i.product.image)" />
                          <img v-else-if="i.items && i.items[0] && i.items[0].product && i.items[0].product.image"
                            :src="userImg(i.items[0].product.image)" />
                          <img v-else-if="i.event && i.event.image" :src="userImg(i.event.image)" />
                          <img v-else :src="assets('logo-circular-watermarked.png')" />
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>
                            <div v-if="i.product">
                              {{ i.product.name }}
                              <span class="ml-1 mr-1">●</span>
                              <span>
                                <v-icon size="15" color="accent"
                                  v-if="i.coupon && i.coupon.discountedTotal > 0">mdi-tag-outline</v-icon>{{
                                    currencySymbol
                                    + getTransactionTotal(i) }}
                              </span>
                              <span class="text-caption ml-1" v-if="i.product && i.product.size">({{ i.product.size
                              }})</span>
                            </div>
                            <div v-else-if="i.items">
                              <span v-if="i.items[0] && i.items[0].name && i.items.length == 1"> {{ i.items[0].name
                              }}</span>
                              <span v-if="i.items[0] && i.items[0].product && i.items.length == 1">
                                {{ i.items[0].product.name }}</span>
                              <span v-else>{{ i.items.length }} {{ $t('message.items') }}</span>
                              <span class="ml-1 mr-1">●</span>
                              <span>
                                <v-icon size="15" color="accent"
                                  v-if="i.coupon && i.coupon.discountedTotal > 0">mdi-tag-outline</v-icon>
                                {{ currencySymbol + getTransactionTotal(i) }}
                              </span>
                              <span class="text-caption ml-1"
                                v-if="i.items[0] && i.items[0].size && i.items.length == 1">({{ i.items[0].size
                                }})</span>
                            </div>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ i.buyer ? i.buyer.displayName : i.email }}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle v-if="i.time">
                            {{ $d(new Date(i.time), 'short', $i18n.locale) }}
                            <span v-if="!isDesktopTransaction(i) && i.expired" class="supporting-text">
                              ({{ $t('message.expired') }})
                            </span>
                            <span v-else-if="!isDesktopTransaction(i) && !i.cancelled && !i.completed"
                              class="supporting-text">
                              ({{ $t('message.pending_payment') }})
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action v-if="false">
                          <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                              <v-btn elevation="0" v-on="on" fab text small slot="activator" left>
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item v-if="i.items && i.items.length > 0" @click.stop="goToTransaction(i)">
                                <v-list-item-title>
                                  {{ $t('message.view') }}
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item v-else
                                @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } })">
                                <v-list-item-title>
                                  {{ $t('message.view') }}
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < transactionsFiltered.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                  <div v-else>
                    <v-data-table item-key="_id" :headers="transactionsHeaders" :items="transactionsFiltered"
                      class="elevation-0" :no-data-text="$t('message.no_data')" :footer-props="{
                        itemsPerPageText: $t('message.rows_per_page'),
                        itemsPerPageOptions: [25, 50, 75],
                      }">
                      <template v-slot:item.processedBy="{ item }">
                        <TransactionIcon @click="goToOneTimeTransaction(item)" :transaction="item"></TransactionIcon>
                      </template>
                      <template v-slot:item.buyer="{ item }">
                        <member-avatar v-if="item.buyer"
                          :image="item.buyer && item.buyer.userImageSmall ? item.buyer.userImageSmall : null" size="30">
                        </member-avatar>
                        <span v-if="item.buyer" class="clickable"
                          @click="goToMember(item.buyer._id, item.buyer.displayName)">
                          {{ item.buyer.displayName }}
                        </span>
                        <span v-else class="clickable">
                          {{ item.email }}
                        </span>
                      </template>
                      <template v-slot:item.items="{ item }">
                        <div @click="goToOneTimeTransaction(item)">
                          <v-avatar size="30" class="mr-2">
                            <img v-if="item.product && item.product.image" :src="userImg(item.product.image)" />
                            <img
                              v-else-if="item.items && item.items[0] && item.items[0].product && item.items[0].product.image"
                              :src="userImg(item.items[0].product.image)" />
                            <img v-else-if="item.event && item.event.image" :src="userImg(item.event.image)" />
                            <img v-else :src="assets('logo-circular-watermarked.png')" />
                          </v-avatar>
                          <span class="clickable" v-if="item.product"
                            @click.stop="navigateTo({ name: 'viewtransaction', params: { sessionId: item._id } })">
                            {{ item.product.name }} {{ item.product.size ? " (" + item.product.size + ")" : "" }}
                          </span>
                          <span class="clickable"
                            v-else-if="item.items[0] && item.items[0].product && item.items.length == 1">
                            {{ item.items[0].product.name }} {{ item.items[0].product.size ? " (" +
                              item.items[0].product.size + ")" : "" }}</span>
                          <span class="clickable"
                            v-else-if="item.items[0] && item.items[0].name && item.items.length == 1">
                            {{ item.items[0].name }} {{ item.items[0].size ? " (" + item.items[0].size + ")" : ""
                            }}</span>
                          <span v-else>{{ item.items.length }} {{ $t('message.items') }}</span>

                        </div>
                      </template>
                      <template v-slot:item.completed="{ item }">
                        <v-chip @click="goToOneTimeTransaction(item)" :color="getStatusColor(item)" small
                          class="white--text">
                          <v-icon v-if="item.expired || item.cancelled">mdi-close-circle-outline</v-icon>
                          <v-icon v-else-if="item.completed">mdi-check-circle-outline</v-icon>
                          <v-icon v-else>mdi-dots-horizontal</v-icon>
                        </v-chip>
                      </template>
                      <template v-slot:item.time="{ item }">
                        <span v-if="item.time">
                          {{ $d(new Date(item.time), 'month_day_year', $i18n.locale) }}
                        </span>
                      </template>
                      <template v-slot:item.amount="{ item }">
                        <v-icon size="15" color="accent"
                          v-if="item.coupon && item.coupon.discountedTotal > 0">mdi-tag-outline</v-icon>
                        {{ currencySymbol }}{{ getTransactionTotal(item) }}
                      </template>
                    </v-data-table>
                  </div>
                </div>
              </div>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'invoices'" v-allowed="'view_income'">
            <v-card elevation="0" class="pa-2">
              <v-row>
                <v-col cols="12" sm="12" md="6">
                  <div class="text-subtitle-2">
                    {{ $t('message.from') }}:
                  </div>
                  <div>
                    <DatePicker :show-time="false" v-model="fromFilter" :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <div class="text-subtitle-2">
                    {{ $t('message.to') }}:
                  </div>
                  <div>
                    <DatePicker :show-time="false" v-model="toFilter" :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
                <v-col colrs="12" sm="12">
                  <v-autocomplete hide-details dense outlined v-if="customers && customers.length > 0"
                    :label="$t('message.customer')" :items="customers" item-text="userName"
                    item-value="stripeCustomerId" v-model="customerFilter" clearable></v-autocomplete>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-btn outlined elevation="0" small color="primary" @click="filterInvoices(false)">
                  <v-icon size="20" class="mr-1">mdi-filter-outline</v-icon>
                  {{ $t('message.filter') }}
                </v-btn>
                <v-btn elevation="0" small color="terciary" @click="filterInvoices(true)">
                  <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                  {{ $t('message.report') }}
                </v-btn>
              </v-card-actions>
            </v-card>
            <div v-if="(stripeReady || paymentsModule == 'easypay') && loadingInvoices" class="text-center mt-2">
              <v-skeleton-loader
                type="list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line">
              </v-skeleton-loader>
            </div>
            <v-list dense v-if="invoicesTotal > 0">
              <div class="supporting-text" v-if="invoicesTotal >= 99">{{
                $t('message.showing_first_results', [invoicesTotal]) }}</div>
              <template v-for="(i, index) in invoices">
                <v-list-item :key="'invoice' + index" class="text-left">
                  <v-list-item-action>
                    <v-icon v-if="i.subscription" size="20">mdi-sync</v-icon>
                    <v-icon v-else size="20">mdi-credit-card-outline</v-icon>
                  </v-list-item-action>
                  <v-list-item-content text-xs-left>
                    <v-list-item-title>
                      <b class="mr-1">{{ currencySymbol + i.amountPaid }}</b>
                      <span v-if="i.productDisplay">{{ i.productDisplay }}</span>
                      <span v-else>
                        {{ i.subscription || (!i.product && !i.items) ? i.itemsDisplay : (i.product ? i.product :
                          i.items
                          +
                          ' ' + $t('message.items')) }}
                        <v-icon size="15" color="accent" v-if="i.discount">
                          mdi-tag-outline
                        </v-icon>
                      </span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ i.userName ? i.userName : i.user }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $d(new Date(i.date), 'long', $i18n.locale) }}
                    </v-list-item-title>
                    <v-list-item-title v-if="i.refunded">
                      <span class="error--text">{{ currencySymbol + i.refundedAmount }}
                        {{ $t('message.refunded') }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn elevation="0" v-on="on" icon small slot="activator" left>
                          <v-icon size="20">mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item v-if="i.transaction" @click.stop="viewInvoiceTransaction(i)">
                          <v-list-item-title>
                            {{ $t('message.view') }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item v-allowed="'issue_refund'" v-if="!i.refunded" @click.stop="showRefundDialog(i)">
                          <v-list-item-title>
                            {{ $t('message.refund') }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < invoices.length" :key="index"></v-divider>
              </template>
            </v-list>
            <v-card-text v-else-if="!loadingInvoices" class="text-center text-h6">
              {{ $t('message.select_time_start') }}
            </v-card-text>
          </v-tab-item>
          <v-tab-item :key="'expenses'" v-allowed="'view_expenses'">
            <v-expansion-panels v-if="false">
              <v-expansion-panel style="margin-top:10px;margin-bottom:10px">
                <v-expansion-panel-header>
                  <div class="text-left">
                    {{ $t('message.filters') }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <v-autocomplete outlined dense v-model="adminFilter" :items="admins"
                      :label="$t('message.filter_by_member')" item-text="text" item-value="value" clearable>
                    </v-autocomplete>
                  </v-card-text>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-card-actions v-if="isSmallerDisplay()">
              <v-btn outlined block elevation="0" small @click="addNewExpense()" color="primary">{{
                $t('message.add_expense')
              }}</v-btn>
            </v-card-actions>
            <v-card-actions v-else>
              <v-spacer></v-spacer>
              <v-btn outlined elevation="0" small @click="addNewExpense()" color="primary">{{ $t('message.add_expense')
              }}</v-btn>
            </v-card-actions>
            <v-row v-if="expensesGraphs.ready">
              <v-col v-if="expensesGraphs.thisYear.data">
                <div class="text-subtitle-2 text-center">{{ $t('message.year_to_date') }}</div>
                <ApexChart type="pie" :options="expensesGraphs.thisYear.options" height="350"
                  :series="expensesGraphs.thisYear.data">
                </ApexChart>
              </v-col>
              <v-col v-if="expensesGraphs.thisMonth.data">
                <div class="text-subtitle-2 text-center">{{ $t('message.month_to_date') }}</div>
                <ApexChart type="pie" :options="expensesGraphs.thisMonth.options" height="350"
                  :series="expensesGraphs.thisMonth.data">
                </ApexChart>
              </v-col>
            </v-row>
            <div class="pa-2">
              <v-select clearable outlined @change="getExpenses" dense v-model="expensesTagFilter" :items="expenseTags"
                hide-details :label="$t('message.category')"></v-select>
            </div>
            <div class="pa-2">
              <v-row>
                <v-col cols="12" sm="12" md="6">
                  <div class="text-subtitle-2">
                    {{ $t('message.from') }}:
                  </div>
                  <div>
                    <DatePicker :show-time="false" v-model="fromFilter" :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                  <div class="text-subtitle-2">
                    {{ $t('message.to') }}:
                  </div>
                  <div>
                    <DatePicker :show-time="false" v-model="toFilter" :past-event="true">
                    </DatePicker>
                  </div>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer v-if="!isSmallerDisplay()"></v-spacer>
                <v-btn color="primary" :block="isSmallerDisplay() ? true : false" outlined @click="getExpenses()">{{
                  $t('message.filter') }}</v-btn>
              </v-card-actions>
            </div>
            <v-skeleton-loader v-if="loadingExpenses"
              type="list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line">
            </v-skeleton-loader>
            <div v-else>
              <v-list v-if="expenses && expenses.length > 0 && expensesGraphs.ready" class="text-left">
                <v-subheader>
                  {{ $t('message.expenses') }} ({{ expensesFiltered.length }})
                </v-subheader>
                <template v-for="(i, index) in expensesFiltered">
                  <v-list-item :key="'expense' + index" @click.stop="goToExpense(i)">
                    <v-list-item-avatar>
                      <ImageDisplay v-if="i.user.userImageSmall" :src="i.user.userImageSmall"></ImageDisplay>
                      <img v-else :src="assets() + '/default-profile-avatar.png'" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        <div>
                          <span class="mr-1">{{ currencySymbol + i.amount * -1 }}</span>
                          <span class="gray--text" v-if="i.items && i.items[0]">{{ i.items[0].name }}</span>
                        </div>
                      </v-list-item-title>
                      <v-list-item-subtitle style="font-size: 14px;" v-if="i.time">{{ $d(new
                        Date(i.completed ?
                          i.completed : i.time), 'long',
                        $i18n.locale) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="i.tags" class="mt-1">
                        <v-chip class="mr-1" :color="i.color" small v-for="(t, index) in i.tags"
                          :key="'expense_tag_' + index">{{ t }}</v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon size="20" v-if="i.receipt">mdi-receipt-text-outline
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-avatar v-if="i.buyer && i.buyer.userImageSmall">
                      <ImageDisplay :src="i.buyer.userImageSmall"></ImageDisplay>
                    </v-list-item-avatar>
                  </v-list-item>
                  <v-divider v-if="index + 1 < expensesFiltered.length" :key="index"></v-divider>
                </template>
              </v-list>
            </div>
          </v-tab-item>
        </v-tabs>
      </div>
    </div>
    <v-dialog v-model="detailedReportDialog" max-width="90%">
      <v-card style class="text-xs-left pa-3">
        <v-card-title>
          {{ $t('message.download_report') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="12">
              <v-switch hide-details v-model="byDatesReport" :label="$t('message.dates')"></v-switch>
            </v-col>
          </v-row>
          <div v-if="!byDatesReport" class="pa-1">
            <v-select outlined dense v-model="monthAmount" :items="availableMonths" hide-details
              :label="$t('message.months')"></v-select>
          </div>
          <div v-else class="pa-1">
            <v-row>
              <v-col cols="12" sm="12" md="6">
                <div class="text-subtitle-2">
                  {{ $t('message.from') }}:
                </div>
                <div>
                  <DatePicker :show-time="false" v-model="fromFilter" :past-event="true">
                  </DatePicker>
                </div>
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <div class="text-subtitle-2">
                  {{ $t('message.to') }}:
                </div>
                <div>
                  <DatePicker :show-time="false" v-model="toFilter" :past-event="true">
                  </DatePicker>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-switch hide-details :disabled="expensesOnly || membershipsOnly ? true : false" v-model="retailOnly"
            :label="$t('message.retail_only')"></v-switch>
          <v-switch hide-details :disabled="retailOnly || membershipsOnly ? true : false" v-model="expensesOnly"
            :label="$t('message.expenses_only')"></v-switch>
          <v-switch hide-details :disabled="retailOnly || expensesOnly ? true : false" v-model="membershipsOnly"
            :label="$t('message.memberships_only')"></v-switch>
          <v-switch hide-details v-model="groupByMonth" :disabled="groupByCategory ? true : false"
            :label="$t('message.group_by_month')"></v-switch>
          <v-switch hide-details :disabled="groupByMonth ? true : false" v-model="groupByCategory"
            :label="$t('message.group_by_category')"></v-switch>
          <v-alert class="mt-1" v-if="groupByCategory" type="info">{{ $t('message.group_by_category_explained')
          }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" color="primary" @click="downloadAllTransactions()">{{ groupByCategory ||
            groupByMonth ? $t('message.aggregated') : $t('message.detailed') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="detailedReportDialog = false">{{ $t('message.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="refundConfirmDialog" max-width="600">
      <v-card style class="text-xs-left pa-3">
        <v-card-text>
          <h2>{{ $t('message.refund_confirm') }}</h2>
        </v-card-text>
        <v-card-text>
          <v-text-field :label="$t('message.amount')" outline v-model="refundAmount"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" color="primary" @click="refund()">{{ $t('message.refund') }}</v-btn>
          <v-btn elevation="0" @click="refundConfirmDialog = false">{{ $t('message.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="connectPaymentDialog" max-width="600">
      <v-card style class="pa-3" outlined>
        <v-card-text class="text-h5 text-center">
          {{ $t('message.payments_soon') }}
        </v-card-text>
        <v-card-text v-if="paymentsModule == 'infinicept'" class="text-center mt-1">
          <div v-html="$t('message.partnering_infinicept')"></div>
          <div class="text-center mt-2">
            <img width="200px" :src="assets('logos/launchpay-logo.png')" />
          </div>
        </v-card-text>
        <v-card-text v-else>
          <div v-html="$t('message.partnering_stripe')"></div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined small elevation="0" color="primary" @click="learnMore()">
            {{ $t('message.know_more') }}
          </v-btn>
          <v-btn outlined small elevation="0" v-if="paymentsModule == 'infinicept' && stripe_state && !stripeReady"
            color="primary" @click="navigateTo({ name: 'onboarding-infinicept' })" style="margin-left:5px">
            {{ $t('message.connect_payments') }}
          </v-btn>
          <v-btn outlined small elevation="0" v-else-if="!isSmallerDisplay() && stripe_state && !stripeReady"
            color="primary" @click="connectPayments()" style="margin-left:5px">
            {{ $t('message.connect_payments') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" @click="connectPaymentDialog = false">
            {{ $t('message.close') }}
          </v-btn>
        </v-card-actions>
        <v-card-text class="text-center text-caption" v-if="paymentsModule != 'infinicept'">
          *{{ $t('message.connecting_payments_agreement') }}
        </v-card-text>
        <v-card-text v-if="isSmallerDisplay()" class="text-center text-caption error--text">
          {{ $t('message.desktop_to_connect_payments') }}
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GraphLoader from "@/components/displays/GraphLoader.vue";
import MemberAvatar from "@/components/displays/MemberAvatar.vue";
import TransactionIcon from "@/components/displays/TransactionIcon.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import TagsSelect from "@/components/inputs/TagsSelect.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { Environment } from "@/services/Environment";
import ApexChart from 'vue-apexcharts';

export default {
  components: {
    MemberAvatar,
    TransactionIcon,
    DatePicker,
    TagsSelect,
    GraphLoader,
    ApexChart
  },
  data() {
    return {
      state: this.$route.query.state,
      scope: this.$route.query.scope,
      code: this.$route.query.code,
      sessionId: this.$route.query.s,
      sessionStatus: this.$route.query.status,
      urlTab: this.$route.params.tabId,
      loading: false,
      academy: null,
      paymentsReady: false,
      productsResultByGraph: false,
      incomeBarQuarterly: false,
      byDatesReport: false,
      transactions: [],
      subscriptions: [],
      transactionsFiltered: [],
      expensesFiltered: [],
      expensesGraphs: { ready: false, thisMonth: {}, thisYear: {}, colors: [] },
      productsGraph: { ready: false, thisYear: {} },
      subscriptionsFiltered: [],
      paymentsModule: null,
      connectPaymentDialog: false,
      retailOnly: false,
      expensesOnly: false,
      membershipsOnly: false,
      detailedReportDialog: null,
      groupByCategory: false,
      loadingMembershipsTable: false,
      groupByMonth: false,
      cancelledFiltered: [],
      stripe_client_id: Environment.get("VUE_APP_STRIPE_CLIENT_ID"),
      stripe_state: null,
      currency: null,
      userFilter: null,
      monthAmount: 12,
      availableMonths: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36],
      adminFilter: null,
      productFilter: null,
      pendingFilter: null,
      fromFilter: null,
      toFilter: null,
      totalAmountTitle: this.$t('message.total_recurrent_income'),
      totalCountTitle: this.$t('message.total_recurrent_memberships'),
      customerFilter: null,
      currencySymbol: '$',
      activeTab: 0,
      expensesTagFilter: null,
      expenseTags: [],
      subscriptionUserFilter: null,
      subscriptionProductFilter: null,
      subscriptionTagFilter: null,
      selectedInvoice: null,
      refundConfirmDialog: false,
      selectedEndMonth: null,
      loadingProductTimeline: false,
      selectedStartMonth: null,
      customerSince: null,
      loadingBottomCharts: false,
      refundAmount: null,
      currentMonthTotal: 0,
      selectedIncomeGraphMonths: 12,
      productTimelineGraph: null,
      availableTags: [],
      admins: [],
      members: [],
      products: [],
      membershipProducts: [],
      invoices: [],
      expenses: [],
      loadingCurrentMonthIncome: false,
      loadingExpenses: false,
      payoutsHistory: {
        loading: true,
        headers: [],
        data: []
      },
      invoicesTotal: 0,
      pieChartSelection: null,
      today: null,
      loadingOneTimeTransactions: false,
      loadingCancelledSubscriptions: false,
      downloadingAllTransactions: false,
      stripeReady: false,
      loadingInvoices: true,
      loadingIncomeTrendline: false,
      triggeringRefresh: false,
      customers: null,
      chartOptionsIncomeMonthly: {},
      chartOptionsIncomeLastTwo: {},
      incomeTrendline: null,
      incomeBars: null,
      subscriptionsFilter: null,
      totalRecurrentIncome: 0,
      downloadingMembershipsTable: false,
      subscriptionsHeaders: [
        { text: this.$t("message.sold_to"), value: "buyer" },
        { text: this.$t("message.subscription"), value: "productName" },
        { text: this.$t("message.sale_date"), value: "time" },
        { text: this.$t("message.frequency"), value: "productInterval" },
        { text: this.$t("message.billing_day"), value: "displayDate" },
        { text: this.$t("message.status"), value: "lastPaymentStatus" },
        { text: this.$t("message.price"), value: "amount" },
      ],
      transactionsHeaders: [
        { text: "", value: "processedBy" },
        { text: this.$t("message.sold_to"), value: "buyer" },
        { text: this.$t("message.items"), value: "items" },
        { text: this.$t("message.date"), value: "time" },
        { text: this.$t("message.status"), value: "completed" },
        { text: this.$t("message.price"), value: "amount" },
      ],
      membershipsTrendlineToggle: false,
      chartDataIncomeMonthly: false,
      membershipsTrendline: {
        options: {},
        data: [],
        membershipCounts: {},
        categories: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31]
      }
    };
  },
  mounted() {
    if (this.urlTab)
      this.selectTab(this.urlTab);

    if (!this.$store.state.managedAcademy) {
      this.navigateTo({ name: "noacademy" });
      return;
    }
    var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    this.today = new Date(Date.now() - tzoffset).toISOString().slice(0, -1);
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        this.academy = response.data;
        this.currency = this.academy.currency;
        this.paymentsModule = response.data.paymentsModule;
        this.currencySymbol = this.academy.currencySymbol;
        this.customerSince = response.data.customerSince;
        let monthsBetween = this.monthsBetween(new Date(), this.customerSince);
        if (monthsBetween > 1) {
          if (monthsBetween > 30)
            monthsBetween = 36;
          this.availableMonths = [];
          for (let i = 1; i < monthsBetween + 1; i++) {
            this.availableMonths.push(i);
          }
        }
        if (this.urlTab == 4) {
          this.loadExpensesCharts();
          this.getExpenses();
        } else if (this.urlTab == 0 || !this.urlTab) {
          this.loadProductsCharts();
        }
        Api2()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/is_connected/stripe"
          )
          .then((response) => {
            this.stripe_state = response.data.stripeState;
            if (this.paymentsModule == 'infinicept')
              this.stripe_state = 'Infini';
            this.stripeReady = response.data.stripeConnected;
            this.paymentsReady = response.data.paymentsReady;
            //console.log('paymentsReady: ', this.paymentsReady);
            this.checkRedirected();
            if (this.stripeReady) {
              this.$store.dispatch("setStripeAccountId", response.data.id);
              if (this.sessionStatus == "success") {
                //Push completed
                if (this.sessionId) {
                  this.completePurchase(this.sessionId);
                } else {
                  this.showAlert(
                    "success",
                    this.$t("message.payment_completed")
                  );
                }
              } else if (this.sessionId && this.sessionStatus == "cancel") {
                this.cancelPurchase(this.sessionId);
              }
            } else {
              this.loading = false;
            }
            this.getTransactions();
            this.getBalances();
          });
      });
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      })
      .catch((e) => {
        if (e)
          this.showAlert(
            "error",
            "Oops!",
            "Looks like you should not be here."
          );
      });
    this.getCustomers();
    this.getProducts();
  },
  computed: {
    subscriptionsFilters() {
      const always = [
        { text: this.$t('message.all'), value: null },
        { text: this.$t('message.expiring_soon'), value: 'expiring_soon' },
        { text: this.$t('message.expired'), value: 'expired' },
      ];
      if (!this.stripeReady) return always;
      return [
        { text: this.$t('message.all'), value: null },
        { text: this.$t('message.good_standing'), value: 'active' },
        { text: this.$t('message.past_due'), value: 'past_due' },
        { text: this.$t('message.waiting_to_start'), value: 'waiting2start' },
        { text: this.$t('message.pending_payment'), value: 'pending' },
        { text: this.$t('message.paused'), value: 'paused' },
        { text: this.$t('message.show_manual_only'), value: 'manual' },
        { text: this.$t('message.expiring_soon'), value: 'expiring_soon' },
        { text: this.$t('message.expired'), value: 'expired' },
        { text: this.$t('message.scheduled_cancellation'), value: 'scheduled_cancellation' },
        { text: this.$t('message.scheduled_pause'), value: 'scheduled_pause' },
      ];
    },
  },
  watch: {
    adminFilter: {
      handler: function () {
        this.filterExpenses();
      },
    },
    userFilter: {
      handler: function (newValue) {
        this.filterTransactions(newValue);
      },
    },
    productFilter: {
      handler: function (newValue) {
        this.productsResultByGraph = false;
        this.filterTransactions(newValue);
      },
    },
    pendingFilter: {
      handler: function () {
        this.getOnetimeTransactions();
      },
    },
    subscriptionsFilter: {
      handler: function (val) {
        this.filterSubscriptions(val);
      },
    },
  },
  methods: {
    getProductTimline() {
      if (this.productsResultByGraph) {
        let data = {
          product: this.productFilter,
          now: new Date(),
          months: 12
        };
        this.loadingProductTimeline = true;
        Api2()
          .post("payments/managedacademy/" + this.$store.state.managedAcademy + "/products/timeline", data)
          .then((response) => {
            if (response.data && response.data.timeline.noData) {
              this.productTimelineGraph = {
                noData: true
              }
            } else {
              // 1. Map the ISO strings to localized strings using your translation function
              const localizedCategories = response.data.timeline.dates.map(dateStr => {
                return this.$d(new Date(dateStr), 'month_year', this.$i18n.locale);
              });

              this.productTimelineGraph = {
                data: [{
                  name: this.$t("message.income"),
                  data: response.data.timeline.data
                }],
                options: {
                  chart: {
                    type: 'bar',
                    toolbar: { show: false },
                    background: 'none'
                  },
                  // Required to make dataLabels stay on top of the bars
                  plotOptions: {
                    bar: {
                      dataLabels: {
                        position: 'top',
                      },
                    }
                  },
                  dataLabels: {
                    enabled: true,
                    offsetY: -20,
                    style: {
                      fontSize: '12px',
                      // Ensuring labels match theme mode for visibility
                      colors: [this.isDarkMode() ? '#FFFFFF' : '#303030']
                    },
                    formatter: (val) => {
                      return this.currencySymbol + val.toLocaleString();
                    }
                  },
                  colors: [this.themeColor('secondary')],
                  stroke: {
                    width: [0, 0, 0, 4, 0],
                    curve: 'smooth'
                  },
                  // Using the translated categories here
                  xaxis: {
                    categories: localizedCategories,
                    axisBorder: { show: false },
                    axisTicks: { show: false }
                  },
                  yaxis: {
                    show: false,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false }
                  },
                  theme: { mode: this.isDarkMode() ? 'dark' : 'light' },
                  // Added padding top so the currency labels at the highest bar aren't clipped
                  grid: {
                    show: false,
                    padding: {
                      top: 25
                    }
                  },
                  legend: {
                    show: false,
                  },
                  tooltip: {
                    shared: true,
                    intersect: false,
                    y: { formatter: (val) => this.currencySymbol + val.toFixed(2) }
                  }
                }
              };
            }
            this.loadingProductTimeline = false;
          });
      }
    },
    filterDatedIncome() {
      this.loadingBottomCharts = true;
      const d = new Date();

      d.setDate(1); // Set to the 1st to prevent overflow (e.g., Jan 30 -> Feb 30)
      d.setMonth(d.getMonth() - this.selectedIncomeGraphMonths);
      this.loadProductsCharts(d);
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/aggregates/v3/trendline?months=" + this.selectedIncomeGraphMonths
        )
        .then((response) => {
          if (response && response.data.income && response.data.income.length) {
            this.buildIncomeBars(response.data.income);
            this.loadingBottomCharts = false;
          }
          this.loadingIncomeTrendline = false;
        });
    },
    downloadMembershipsTable() {
      this.downloadingMembershipsTable = true;
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/memberships/csv"
        )
        .then((response) => {
          this.downloadingMembershipsTable = false;
          let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            this.academy.name +
            "_Memberships_" +
            new Date().toISOString() +
            "_" +
            ".csv";
          link.click();
          this.showAlert("success", this.$t("message.success"));
        }).catch((err) => {
          this.downloadingMembershipsTable = false;
          if (err)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          this.downloadingAllTransactions = false;
        });
    },
    colorExpenses() {
      if (this.expenses && this.expenses[0] && !this.expenses[0].color) {
        for (let expense of this.expenses) {
          const cat = expense.tags && expense.tags.length > 0 ? expense.tags[0] : null;
          if (cat) {
            expense.color = this.expensesGraphs.colors[cat];
          }
        }
      }
    },
    loadExpensesCharts() {
      let d1 = new Date();
      let d2 = new Date();
      d1.setDate(1);
      d1.setHours(0);
      d1.setMinutes(0);
      d2.setMonth(0);
      d2.setDate(1);
      d2.setHours(0);
      d2.setMinutes(0);
      Api()
        .get("payments/academy/" + this.$store.state.managedAcademy + "/expenses/aggregates?d1=" + d1 + "&d2=" + d2)
        .then((response) => {
          const data = response.data;
          if (data) {
            let ready = false;
            //expensesGraphs
            let vm = this;
            let allCategories = data.data.categories;
            const colors = [
              this.themeColor('gold'),
              this.themeColor('orange'),
              this.themeColor('blue'),
              this.themeColor('success'),
              this.themeColor('yellow'),
              this.themeColor('accent'),
              this.themeColor('brown'),
              this.themeColor('silver'),
              this.themeColor('secondary')
            ];
            let colorsMap = {};
            let index = 0;
            for (const cat of allCategories) {
              if (index <= 8) {
                colorsMap[cat] = colors[index];
              } else {
                const randomNum = Math.floor(Math.random() * 9);
                colorsMap[cat] = this.getRandomColorVariation(colors[randomNum]);
              }
              index++;
            }
            this.expensesGraphs.colors = colorsMap;
            if (data.data.thisMonth) {
              ready = true;

              let graphColorsThisMonth = [];
              let graphData = [];
              let thisMonthLabels = [];
              for (const i of data.data.thisMonth) {
                graphData.push(i.total);
                thisMonthLabels.push(i.category);
                graphColorsThisMonth.push(colorsMap[i.category]);
              }
              this.expensesGraphs.thisMonth.options = {
                legend: {
                  position: 'bottom'
                },
                stroke: { show: false },
                fill: {
                  colors: graphColorsThisMonth,
                },
                theme: {
                  mode: this.isDarkMode() ? 'dark' : 'light',
                },
                chart: { background: 'none' },
                labels: thisMonthLabels,
                tooltip: {
                  y: {
                    formatter: function (value) {
                      return vm.currencySymbol + '' + value;
                    }
                  },
                },
              };
              this.expensesGraphs.thisMonth.data = graphData;
            }
            if (data.data.thisYear) {
              ready = true;

              let graphColorsThisYear = [];
              let graphData = [];
              let thisYearLabels = [];
              for (const i of data.data.thisYear) {
                graphData.push(i.total);
                thisYearLabels.push(i.category);
                graphColorsThisYear.push(colorsMap[i.category]);
              }
              this.expensesGraphs.thisYear.options = {
                legend: {
                  position: 'bottom'
                },
                stroke: { show: false },
                theme: {
                  mode: this.isDarkMode() ? 'dark' : 'light',
                },
                fill: {
                  colors: graphColorsThisYear,
                },
                chart: { background: 'none' },
                labels: thisYearLabels,
                tooltip: {
                  y: {
                    formatter: function (value) {
                      return vm.currencySymbol + '' + value;
                    }
                  },
                },
              };
              this.expensesGraphs.thisYear.data = graphData;
              //console.log('graphColorsThisYear: ', graphColorsThisYear);
            }
            if (ready) {
              this.colorExpenses();
            }
            this.expensesGraphs.ready = true;
          }
        });
    },
    loadProductsCharts(d1) {
      if (!d1) {
        d1 = new Date();
        d1.setDate(1); // Set to the 1st to prevent overflow (e.g., Jan 30 -> Feb 30)
        d1.setMonth(d1.getMonth() - 12);
      } else {
        d1.setHours(0);
        d1.setMinutes(0);
      }
      Api2()
        .get("payments/academy/" + this.$store.state.managedAcademy + "/products/aggregates?d1=" + d1)
        .then((response) => {
          const data = response.data;
          const currencySymbol = this.currencySymbol;
          let productAmounts = [];
          const sold = this.$t('message.sold');
          const smallDisplay = this.isSmallerDisplay();
          let vm = this;
          if (data) {
            this.productsGraph.products = {};
            if (data.data.thisYear && data.data.thisYear.length > 0) {
              let graphData = [];
              let categories = [];
              for (const i of data.data.thisYear) {
                graphData.push(i.totalCounts);
                productAmounts.push(i.totalAmount);
                categories.push(i.name);
              }
              this.productsGraph.thisYear.options = {
                legend: {
                  show: smallDisplay ? false : true,
                  position: smallDisplay ? 'bottom' : 'right'
                },
                stroke: { show: false },
                theme: {
                  mode: this.isDarkMode() ? 'dark' : 'light',
                  monochrome: {
                    enabled: true,
                    color: this.themeColor('secondary')
                  }
                },
                chart: {
                  background: 'none',
                  events: {
                    dataPointSelection: (event, chartContext, config) => {
                      if (vm.isSmallerDisplay()) {
                        const idx = config.dataPointIndex;

                        // 2. Access your data using that index
                        const label = categories[idx];
                        const count = graphData[idx];
                        const amount = productAmounts[idx];
                        vm.pieChartSelection = {
                          label: label,
                          count: count,
                          revenue: vm.currencySymbol + amount
                        };
                      }
                    }
                  }
                },
                plotOptions: {
                  pie: {
                    expandOnClick: true, // Ensure this is true
                    dataLabels: {
                      offset: -5 // Adjusts label position inside slices
                    }
                  }
                },
                labels: categories,
                tooltip: {
                  y: {
                    formatter: function (value, data) {
                      return value + ' ' + sold + ", " + currencySymbol + productAmounts[data.seriesIndex];
                    }
                  },
                },
              };
              this.productsGraph.thisYear.data = graphData;
              this.productsGraph.ready = true;
            }
          }
        });
    },
    getProducts() {
      Api2()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/products ")
        .then((response) => {
          const data = response.data;
          if (data) {
            //We back
            for (const p of data.goods) {
              if (p.isMembership)
                this.membershipProducts.push({ value: p._id, text: p.name });
              else
                this.products.push({ value: p._id, text: p.name });
            }
            for (const p of data.subscriptions)
              this.membershipProducts.push({ value: p._id, text: p.name });
          }
        })
        .catch((e) => {
          if (e) this.showAlert("error", "Oops!", ":(");
        });
    },
    getPayoutStatusColor(status) {
      //paid, pending, in_transit, canceled or failed
      if (status == 'paid')
        return this.themeColor('success');
      else if (status == 'success')
        return this.themeColor('success');
      else if (status == 'pending')
        return this.themeColor('pending');
      else if (status == 'in_transit') {
        return this.themeColor('gold');
      } else if (status == 'canceled')
        return this.themeColor('error');
      else if (status == 'failed')
        return this.themeColor('error');
      else if (status == 'succeeded') {
        return this.themeColor('success');
      }
    },
    getBalances() {
      this.payoutsHistory.loading = true;
      if (!this.stripeReady) {
        this.payoutsHistory.loading = false;
        return;
      }
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/balances?today=" + (new Date().toISOString())
        )
        .then((response) => {
          if (response.data) {
            this.payoutsHistory.data = response.data.data.payouts;
            this.payoutsHistory.inTransit = response.data.data.inTransit;
            this.payoutsHistory.future = response.data.data.future;
            this.payoutsHistory.headers = [
              { text: this.$t("message.created"), value: "started" },
              { text: this.$t("message.arriving"), value: "arrival" },
              { text: this.$t("message.amount"), value: "amount" },
              { text: this.$t("message.status"), value: "status" },
            ];
            this.payoutsHistory.loading = false;
          }
        })
    },
    subscriptionsSort(items, index, isDesc) {
      items.sort((a, b) => {
        if (index[0] == 'time') {
          if (!isDesc[0])
            return new Date(a[index]) - new Date(b[index]);
          else
            return new Date(b[index]) - new Date(a[index]);
        } if (index[0] == 'displayDate') {
          if (!isDesc[0])
            return a[index] - b[index];
          else
            return b[index] - a[index];
        } else if (index[0] == 'amount') {
          if (!isDesc[0])
            return a[index] - b[index];
          else
            return b[index] - a[index];
        } else if (index[0] == 'lastPaymentStatus') {
          let aVal = a[index] == 'active' ? 1 : (a[index] == 'trialing' ? 2 : (a[index] == 'paused' ? 3 : 4));
          let bVal = b[index] == 'active' ? 1 : (b[index] == 'trialing' ? 2 : (b[index] == 'paused' ? 3 : 4));
          if (!isDesc[0])
            return aVal - bVal;
          else
            return bVal - aVal;
        } else {
          if (typeof a[index] !== 'undefined') {
            let aVal = a[index];
            let bVal = b[index];
            if (index[0] == 'buyer') {
              aVal = a[index].displayName;
              bVal = b[index].displayName;
            }
            if (!isDesc[0]) {
              return aVal.toLowerCase().localeCompare(bVal.toLowerCase());
            }
            else {
              return bVal.toLowerCase().localeCompare(aVal.toLowerCase());
            }
          }
        }
      });
      return items;
    },
    getCancelledSubcriptions() {
      this.cancelledFiltered = [];
      this.loadingCancelledSubscriptions = true;
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/transactions/recurrent/cancelled"
        )
        .then((response) => {
          if (response.data) {
            this.cancelledFiltered = response.data.cancelled;
          }
          this.loadingCancelledSubscriptions = false;
        }).catch((err) => {
          if (err)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    goToOneTimeTransaction(t) {
      if (t.items && t.items.length > 0)
        this.goToTransaction(t);
      else
        this.navigateTo({ name: 'viewtransaction', params: { sessionId: t._id } });
    },
    getOnetimeTransactions() {
      this.loadingOneTimeTransactions = true;
      let params = {};
      if (this.pendingFilter)
        params.pending = true;
      Api()
        .post(
          "payments/academy/" + this.$store.state.managedAcademy + "/transactions/onetime/filter",
          params
        )
        .then((response) => {
          if (response.data) {
            this.transactions = response.data.transactions;
            this.transactionsFiltered = response.data.transactions;
          }
          this.loadingOneTimeTransactions = false;
        }).catch((err) => {
          if (err)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          this.loadingOneTimeTransactions = false;
        });
    },
    goToNewEvent() {
      this.navigateTo({
        name: "manageacademyevent",
        params: { eventId: 'new' },
      });
    },
    goToExpense(item) {
      this.navigateTo({ name: 'manageexpense', params: { transactionId: item._id } });
    },
    addNewExpense() {
      this.navigateTo({ name: 'manageexpense', params: { transactionId: 'new' } });
    },
    goToTransaction(i) {
      if (i) {
        if (i.items && i.items.length > 0)
          this.navigateTo({ name: 'viewitemstransaction', params: { transactionId: i._id } });
        else
          this.navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } })
      }
    },
    selectTab(num) {
      try {
        const n = new Number(num);
        this.activeTab = n.valueOf();
      } catch (err) {
        console.log(err)
        console.log('Invalid tab: ', num)
      }
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        this.activeTab = tab;
        this.getTransactions();
        this.navigateTo({
          name: "transactionstab",
          params: { tabId: tab },
        });
        if (tab == 4) {
          this.loadExpensesCharts();
          this.getExpenses();
        } else if (tab == 0 || !tab) {
          this.loadProductsCharts()
        }
      }
    },
    downloadAggregatedTransactions() {
      this.downloadingAllTransactions = true;
      let params = "";
      if (this.byDatesReport) {
        if (!this.fromFilter)
          this.fromFilter = new Date();
        this.fromFilter = this.toBeginningOfDay(this.fromFilter);
        if (!this.toFilter)
          this.toFilter = new Date();
        this.toFilter = this.toEndOfDay(this.toFilter);
        params = "&from=" + this.fromFilter + '&to=' + this.toFilter;
      } else {
        const data = this.getMonthRange(this.monthAmount);
        params = "&from=" + data.from + '&to=' + data.to;
      }
      if (this.groupByMonth)
        params += '&group_by=month'
      else
        params += '&group_by=category'
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/aggregates/v2?csv=true" +
          (this.retailOnly ? '&retail_only=true' : '') +
          (this.expensesOnly ? '&expenses_only=true' : '') +
          (this.membershipsOnly ? '&membership_only=true' : '') + params
        )
        .then((response) => {
          this.downloadingAllTransactions = false;
          this.showAlert("success", this.$t("message.report_in_progress", [response.data.sendTo]));
        }).catch((err) => {
          if (err)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          this.downloadingAllTransactions = false;
        });
    },
    downloadAllTransactions() {
      if (this.groupByMonth || this.groupByCategory) {
        return this.downloadAggregatedTransactions();
      }
      this.downloadingAllTransactions = true;
      let params = "";
      if (this.byDatesReport) {
        if (!this.fromFilter)
          this.fromFilter = new Date();
        this.fromFilter = this.toBeginningOfDay(this.fromFilter);
        if (!this.toFilter)
          this.toFilter = new Date();
        this.toFilter = this.toEndOfDay(this.toFilter);
        params = "&from=" + this.fromFilter + '&to=' + this.toFilter;
      } else {
        const data = this.getMonthRange(this.monthAmount);
        params = "&from=" + data.from + '&to=' + data.to;
      }
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/aggregates/v2?csv=true&detailed=true" +
          (this.retailOnly ? '&retail_only=true' : '') +
          (this.expensesOnly ? '&expenses_only=true' : '') +
          (this.membershipsOnly ? '&membership_only=true' : '') + params
        )
        .then((response) => {
          this.showAlert("success", this.$t("message.report_in_progress", [response.data.sendTo]));
          this.downloadingAllTransactions = false;
          this.detailedReportDialog = false;
          this.retailOnly = false;
          this.expensesOnly = false;
          this.membershipsOnly = false;
        }).catch((err) => {
          if (err)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          this.downloadingAllTransactions = false;
        });
    },
    viewInvoiceTransaction(i) {
      if (i && i.items > 0) {
        this.navigateTo({
          name: "viewitemstransaction",
          params: { transactionId: i.transaction },
        });
      } else {
        if (i.transaction) {
          this.navigateTo({
            name: "viewtransaction",
            params: { sessionId: i.transaction },
            query: { invoiceId: i.invoice }
          });
        } else {
          this.navigateTo({
            name: "viewpayment",
            params: { paymentIntentId: i.id },
          });
        }
      }
    },
    print(i) {//Not used
      if (i.invoice)
        this.navigateTo({
          name: "viewinvoice",
          params: { invoiceId: i.invoice },
        });
      else
        this.navigateTo({
          name: "viewpayment",
          params: { paymentIntentId: i.id },
        });
    },
    buildIncomeMonthlyData(data) {
      if (!data)
        return;
      let categories = [];
      let graphData = [
        { name: this.$t("message.expenses"), data: [] },
        { name: this.$t("message.retail"), data: [] },
        { name: this.$t("message.events"), data: [] },
        { name: this.$t("message.memberships"), data: [] }
      ];
      const totalMonths = data.length;
      for (let i = totalMonths; i > 0; i--) {
        const month = new Date();
        // Set to the 1st of the month first to avoid overflow issues
        month.setDate(1);
        month.setMonth(month.getMonth() - i);
        categories.push(this.$d(month, "month_year", this.$i18n.locale));
      }
      for (const i of data) {
        graphData[0].data.push(i.expenses);
        graphData[1].data.push(i.retail);
        graphData[2].data.push(i.events);
        graphData[3].data.push(i.memberships);
      }
      this.incomeTrendline = {
        data: graphData,
        options: {
          labels: { show: false },
          colors: [this.themeColor('error'), this.themeColor('graph1'), this.themeColor('graph2'), this.themeColor('secondary')],
          xaxis: {
            categories: categories,
          },
          yaxis: {
            labels: {
              show: true,
            }
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
            width: 3
          },
          theme: {
            mode: this.isDarkMode() ? 'dark' : 'light',
          },
          chart: {
            toolbar: {
              show: false
            },
            background: 'none'
          },
          legend: {
            show: false
          },
          grid: {
            show: false
          },
        }
      }
      this.loadingIncomeTrendline = false;
    },
    buildIncomeBars(data) {
      if (!data && this.incomeBars.raw)
        data = this.incomeBars.raw;
      let categories = [];
      let retail = [], events = [], memberships = [], expenses = [], netTotal = [];

      if (this.incomeBarQuarterly) {
        const quarters = {};

        data.forEach((item, index) => {
          // 1. Calculate the date for this specific data point
          const date = new Date();
          date.setMonth(date.getMonth() - (data.length - index));

          // 2. Create a unique key like "Q1 2025"
          const qNum = Math.floor(date.getMonth() / 3) + 1;
          const qKey = `Q${qNum} ${date.getFullYear()}`;

          // 3. Initialize or sum the values
          if (!quarters[qKey]) {
            quarters[qKey] = { retail: 0, events: 0, memberships: 0, expenses: 0 };
          }
          quarters[qKey].retail += item.retail;
          quarters[qKey].events += item.events;
          quarters[qKey].memberships += item.memberships;
          quarters[qKey].expenses += item.expenses;
        });

        // 4. Map the grouped object back into your arrays
        Object.keys(quarters).forEach(key => {
          const q = quarters[key];
          categories.push(key);
          retail.push(q.retail);
          events.push(q.events);
          memberships.push(q.memberships);
          expenses.push(q.expenses);
          netTotal.push(q.retail + q.events + q.memberships - Math.abs(q.expenses));
        });
      } else {
        for (let i = data.length; i > 0; i--) {
          const month = new Date();
          // Set to the 1st of the month first to avoid overflow issues
          month.setDate(1);
          month.setMonth(month.getMonth() - i);
          categories.push(this.$d(month, "month_year", this.$i18n.locale));
        }
        for (const i of data) {
          retail.push(i.retail);
          events.push(i.events);
          memberships.push(i.memberships);
          expenses.push(i.expenses);
          netTotal.push(i.retail + i.events + i.memberships - Math.abs(i.expenses));
        }
      }

      // Map data to the graphData array
      let graphData = [
        { name: this.$t("message.retail"), type: 'column', data: retail },
        { name: this.$t("message.events"), type: 'column', data: events },
        { name: this.$t("message.memberships"), type: 'column', data: memberships },
        { name: this.$t("message.total_profit"), type: 'line', data: netTotal },
        { name: this.$t("message.expenses"), type: 'line', data: expenses }
      ];

      // Assign to incomeBars
      this.incomeBars = {
        raw: data,
        data: graphData,
        options: {
          chart: {
            type: 'line',
            stacked: true,
            toolbar: { show: false },
            background: 'none'
          },
          // Same options as before...
          colors: [
            this.themeColor('graph1'),
            this.themeColor('graph2'),
            this.themeColor('secondary'),
            this.themeColor('primary'),
            this.themeColor('error'),
          ],
          stroke: {
            width: [0, 0, 0, 4, 0],
            curve: 'smooth'
          },
          xaxis: { categories: categories },
          yaxis: {
            show: false, // Hides the entire axis (numbers, title, and the line)
            labels: {
              show: false // Specifically ensures the numbers are hidden
            },
            axisBorder: {
              show: false // Removes the vertical line on the left
            },
            axisTicks: {
              show: false // Removes the small tick marks
            }
          },
          theme: { mode: this.isDarkMode() ? 'dark' : 'light' },
          grid: { show: false },
          legend: {
            show: false,
          },
          tooltip: {
            shared: true,
            y: { formatter: (val) => this.currencySymbol + val.toFixed(2) }
          }
        }
      };
    },
    buildCurrentMonthGraph(data) {
      this.currentMonthTotal = 0;
      let graphData = [
        { name: 'Total', data: [] },
      ];
      let categories = [this.$t("message.expenses"), this.$t("message.retail"), this.$t("message.events"), this.$t("message.memberships")];
      const currentMonthData = data['month'];
      //categories.push(this.$d(new Date(d.month), 'month_year', this.$i18n.locale));
      graphData[0].data.push(currentMonthData.expense ? Math.abs(currentMonthData.expense).toFixed(2) : 0);
      this.currentMonthTotal += currentMonthData.expense ? currentMonthData.expense : 0;
      graphData[0].data.push(currentMonthData.retail ? currentMonthData.retail.toFixed(2) : 0);
      this.currentMonthTotal += currentMonthData.retail ? currentMonthData.retail : 0;
      graphData[0].data.push(currentMonthData.event ? currentMonthData.event.toFixed(2) : 0);
      this.currentMonthTotal += currentMonthData.event ? currentMonthData.event : 0;
      graphData[0].data.push(currentMonthData.membership ? currentMonthData.membership.toFixed(2) : 0);
      this.currentMonthTotal += currentMonthData.membership ? currentMonthData.membership : 0;
      this.currentMonthTotal = parseFloat(this.currentMonthTotal.toFixed(2));
      const vm = this;
      let chartOptions = {
        grid: {
          show: false
        },
        theme: {
          mode: this.isDarkMode() ? 'dark' : 'light',
        },
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          },
          background: 'none'
        },
        plotOptions: {
          bar: {
            distributed: true, // this line is mandatory
            borderRadius: 4,
            borderRadiusApplication: 'end',
            barHeight: '90%'
          }
        },
        colors: [this.themeColor('error'), this.themeColor('graph1'), this.themeColor('graph2'), this.themeColor('secondary')],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return vm.currencySymbol + val.toFixed(2)
          },
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
          },
          offsetY: -20,
        },
        stroke: {
          show: false
        },
        yaxis: {
          show: false, // Hides the entire axis (title, line, and labels)
          labels: {
            show: false // Specifically ensures the numbers are gone
          },
          axisBorder: {
            show: false // Removes the vertical line on the left
          },
          axisTicks: {
            show: false // Removes the small tick marks
          }
        },
        xaxis: {
          categories: categories,
          show: false,
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: false
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return vm.currencySymbol + val.toFixed(2);
            }
          }
        },
        legend: {
          show: false
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              dataLabels: {
                enabled: true,
                formatter: function (val) {
                  if (val === 0) return '';
                  return vm.currencySymbol + val.toFixed(0)
                },
                style: {
                  fontSize: '9px',
                },
                offsetY: -15,
              },
              plotOptions: {
                bar: {
                  columnWidth: '70%',
                }
              }
            }
          }
        ]
      };
      chartOptions.xaxis.categories = categories;
      chartOptions.lastUpdate = currentMonthData.lastUpdate;
      this.chartOptionsIncomeMonthly = chartOptions;
      this.chartDataIncomeMonthly = graphData;
      this.loadingCurrentMonthIncome = false;
    },
    getCustomers() {
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/customers/credit"
        )
        .then((response) => {
          this.customers = response.data.customers;
        });
    },
    getAggregates() {
      this.loadingCurrentMonthIncome = true;
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/aggregates/v3?months=2"
        )
        .then((response) => {
          if (response && response.data.incomeByMonth && Object.keys(response.data.incomeByMonth).length) {
            const lastKey = Object.keys(response.data.incomeByMonth)[0];
            const lastValue = response.data.incomeByMonth[lastKey];
            this.buildCurrentMonthGraph({ month: lastValue });
            this.loadingCurrentMonthIncome = false;
          } else {
            this.loadingCurrentMonthIncome = false;
          }
        });
      this.loadingIncomeTrendline = true;
      Api2()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/aggregates/v3/trendline?months=12"
        )
        .then((response) => {
          if (response && response.data.income && response.data.income.length) {
            this.buildIncomeMonthlyData(response.data.income);
            this.buildIncomeBars(response.data.income);
          }
          this.loadingIncomeTrendline = false;
        });
    },
    showRefundDialog(invoice) {
      this.selectedInvoice = invoice;
      this.refundConfirmDialog = true;
      this.refundAmount = invoice.amountPaid;
    },
    refund() {
      var data = {
        amount: this.refundAmount,
        transaction: this.selectedInvoice.transaction,
      };
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/refund/" +
          this.selectedInvoice.id,
          data
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
            this.selectedInvoice = null;
            this.getTransactions();
            this.refundConfirmDialog = false;
          }
        })
        .catch((err) => {
          this.showAlert("error", err.response.data.message);
        });
    },
    filterSubscriptions() {
      this.subscriptionsFiltered = [];
      let val = this.subscriptionsFilter;
      if (!val && !this.stripeReady) {
        this.subscriptionsFilter = 'manual';
        val = 'manual';
      }
      if (val == 'manual' || val == 'expiring_soon' || val == 'expired') {
        this.totalAmountTitle = this.$t('message.total_manual_income');
        this.totalCountTitle = this.$t('message.total_manual_memberships');
      } else {
        this.totalAmountTitle = this.$t('message.total_recurrent_income');
        this.totalCountTitle = this.$t('message.total_recurrent_memberships');
      }
      let user = this.subscriptionUserFilter;
      let product = this.subscriptionProductFilter;
      let tag = this.subscriptionTagFilter;
      let totalIncome = 0;
      let trendlineDays = {};
      let membershipCounts = {};
      for (let t of this.subscriptions) {
        t.keep = false;
        if (val) {
          if (val == "active") {
            if (t.lastPaymentStatus == "active" && t.product.type != 'good') t.keep = true;
          } else if (val == "paused") {
            if (t.lastPaymentStatus == "paused") t.keep = true;
          } else if (val == "pending") {
            if (t.lastPaymentStatus == "pending_payment") t.keep = true;
          } else if (val == "past_due") {
            if (t.lastPaymentStatus == "past_due" || t.lastPaymentStatus == "unpaid") t.keep = true;
          } else if (val == "waiting2start") {
            if (t.lastPaymentStatus == "trialing") t.keep = true;
          } else if (val == "scheduled_cancellation") {
            if (t.cancellationDate) t.keep = true;
          } else if (val == "scheduled_pause") {
            if (t.pauseDate) t.keep = true;
          } else if (val == "manual") {
            if (t.product && t.product.type == 'good') t.keep = true;
          } else if (val == "expiring_soon") {
            //End expiring soon
            if (t.product && t.product.type == 'good' && t.expiringSoon) t.keep = true;
          } else if (val == "expired") {
            //End expiring soon
            if (t.product && t.product.type == 'good' && t.lastPaymentStatus == 'expired') t.keep = true;
          }
        } else {
          if (t.product && t.product.type != 'good') t.keep = true;
        }
        if (user && user.length > 1 && t.keep && t.buyer && t.buyer.displayName.toLowerCase().indexOf(user) < 0) {
          user = user.toLowerCase()
          t.keep = false;
        }
        if (tag && tag != "" && (!t.tags || t.tags.length == 0 || t.tags.indexOf(tag) < 0)) {
          t.keep = false;
        }
        if (t.keep && product && t.product && t.product._id != product) {
          t.keep = false;
        }
        if (t.keep) {
          if (t.product) {
            t.productName = t.product.name;
            t.productInterval = t.product.interval;
          }
          this.subscriptionsFiltered.push(t);
          const interval = t.product ? t.product.interval : null;
          //Setup trendline data
          const amount = new Number(this.getMembershipTotal(t)).valueOf();
          if (interval == 'month') {
            if (!trendlineDays[t.displayDate]) {
              trendlineDays[t.displayDate] = 0;
              membershipCounts[t.displayDate] = 0;
            }
            trendlineDays[t.displayDate] += amount;
            membershipCounts[t.displayDate] += 1;
          }
          if (interval == 'bi-week') {
            //We ned to add 14...
          }
          let monthlyAmount = amount;
          if (interval == 'bi-week')
            monthlyAmount = amount * 2; //Twice a month
          else if (interval == 'week')
            monthlyAmount = amount * 4;
          else if (interval == 'bi-year')
            monthlyAmount = amount / 6;
          else if (interval == 'year')
            monthlyAmount = amount / 12;
          else if (interval == 'day')
            monthlyAmount = amount * 30;
          totalIncome += monthlyAmount;
        }
      }
      let trendlineData = [];
      let hasTrendline = false;
      for (const day of this.membershipsTrendline.categories) {
        if (Object.prototype.hasOwnProperty.call(trendlineDays, day)) {
          trendlineData.push(trendlineDays[day]);
          hasTrendline = true;
        } else {
          trendlineData.push(0);
        }
      }
      //trendlineCagetories
      let vm = this;
      if (hasTrendline) {
        const todayDay = new Date();
        vm.membershipsTrendline.membershipCounts = membershipCounts;
        const currency = vm.currencySymbol;
        vm.membershipsTrendline.data = [{
          name: vm.$t("message.memberships"),
          data: trendlineData
        }];
        vm.membershipsTrendline.options = {
          labels: [vm.$t("message.memberships")],
          colors: [vm.themeColor('primary')],
          title: {
            show: true,
            text: vm.$t("message.recurrent_payments"),
            align: 'center',
          },
          xaxis: {
            categories: vm.membershipsTrendline.categories,
            labels: {
              show: true,
            }
          },
          yaxis: {
            labels: {
              show: true,
              formatter: function (value) {
                return currency + value;
              }
            }
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth'
          },
          theme: {
            mode: this.isDarkMode() ? 'dark' : 'light',
          },
          chart: {
            toolbar: {
              show: false
            },
            background: 'none'
          },
          legend: {
            position: 'top'
          },
          grid: {
            show: false
          },
          tooltip: {
            y: {
              formatter: function (value, data) {
                const day = data.dataPointIndex + 1;
                let count = vm.membershipsTrendline.membershipCounts[day];
                if (!count)
                  count = 0;
                return count + ' ' + vm.$t("message.memberships") + ' - ' + currency + value;
              }
            }, x: {
              formatter: function (value) {
                return vm.$t("message.day_of_month") + ': ' + value;
              }
            },
          },
          annotations: {
            xaxis: [
              {
                x: todayDay.getDate(),
                borderColor: vm.themeColor('accent'),
                label: {
                  text: 'Today'
                }
              }
            ]
          }
        };
        this.membershipsTrendline.ready = true;
      }
      this.totalRecurrentIncome = new Number(totalIncome.toFixed(2)).valueOf();
    },
    filterTransactions(filtering) {
      if (!filtering) {
        this.transactionsFiltered = this.transactions;
        this.productFilter = null; // Reset filters if necessary
        this.userFilter = null;
        return;
      }

      this.transactionsFiltered = this.transactions.filter(t => {
        let matchesProduct = true;
        let matchesUser = true;

        // 1. Check Product Filter (Direct product OR within Items array)
        if (this.productFilter) {
          const hasDirectProduct = t.product && t.product._id === this.productFilter;

          const hasProductInItems = t.items && t.items.some(item =>
            item.product && (item.product._id === this.productFilter || item.product === this.productFilter)
          );

          matchesProduct = hasDirectProduct || hasProductInItems;
        }

        // 2. Check User Filter
        if (this.userFilter) {
          matchesUser = t.buyer && t.buyer._id === this.userFilter;
        }

        // 3. Logic: Transaction must satisfy ALL active filters
        return matchesProduct && matchesUser;
      });
    },
    filterExpenses() {
      this.expensesFiltered = [];
      for (var t of this.expenses) {
        if (this.adminFilter) {
          if (t.user && t.user._id == this.adminFilter) t.keep = true;
          else t.keep = false;
        }
        if (t.keep) this.expensesFiltered.push(t);
      }
    },
    filterInvoices(csv) {
      this.loadingInvoices = true;
      if (!csv) {
        this.invoices = [];
        this.invoicesTotal = 0;
      }
      if (!this.fromFilter) {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        this.fromFilter = d;
      }
      if (!this.toFilter) {
        this.toFilter = new Date();
      }
      const from = this.toBeginningOfDay(this.fromFilter);
      const to = this.toEndOfDay(this.toFilter);
      const filters = {
        from: from,
        to: to,
        customer: this.customerFilter,
        csv: csv,
      };
      Api()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/invoices",
          filters
        )
        .then((response) => {
          if (csv) {
            this.showAlert("success", this.$t("message.report_in_progress", [response.data.sendTo]));
          } else {
            this.invoices = response.data.invoices;
            this.invoicesTotal = response.data.total;
          }
          this.loadingInvoices = false;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    getTransactions() {
      if (this.activeTab == 2) {
        if (!this.loadingOneTimeTransactions)
          this.getOnetimeTransactions();
      } else if (this.activeTab == 3 && (this.stripeReady || (this.paymentsModule == 'easypay' && this.paymentsReady))) {
        if (this.loadingInvoices)
          this.filterInvoices(false);
      } else {
        if (!this.loading)
          this.getRecurrentTransactions();
        if (!this.loadingCurrentMonthIncome)
          this.getAggregates();
        this.triggerStatsRefresh();
      }
    },
    triggerStatsRefresh(force) {
      if (this.triggeringRefresh)
        return;
      this.triggeringRefresh = true;
      let lastMonth = null;
      if (force) {
        lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
      }
      Api2() /**TODO: make me not depend on stripe ID */
        .post(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/trigger/monthly/stats", { force: lastMonth }
        ).then(() => {
          this.triggeringRefresh = false;
          if (force)
            this.showAlert("success", this.$t("message.data_refresh_triggered"));
        });
    },
    getRecurrentTransactions(withFilters) {
      let filters = {};
      if (withFilters) {
        if (this.fromFilter) {
          if (!this.toFilter)
            this.toFilter = new Date();
          this.fromFilter = this.toBeginningOfDay(this.fromFilter);
          this.toFilter = this.toEndOfDay(this.toFilter);
          filters = { from: this.fromFilter, to: this.toFilter };
        } else {
          return;
        }
        this.loadingMembershipsTable = true;
      } else {
        this.fromFilter = null;
        this.toFilter = null
        this.loading = true;
      }
      Api2() /**TODO: make me not depend on stripe ID */
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/transactions",
          filters
        )
        .then((response) => {
          this.loading = false;
          this.availableTags = response.data.availableTags;
          let tmp = response.data.subscriptions;
          let expiring = response.data.expiringSoon;
          for (let s of tmp) {
            let sd = new Date(s.time);
            if (s.billingDate) {
              sd = new Date(s.billingDate);
            } else if (s.subscriptionStartDate) {
              sd = new Date(s.subscriptionStartDate);
            }
            s.displayDate = sd.getDate();
            for (const e of expiring) {
              if (s._id == e._id) {
                s.expiringSoon = true;
                break;
              }
            }
          }
          this.subscriptions = tmp;
          if (!this.stripeReady) {
            this.subscriptionsFilter = "manual";
          } else {
            this.filterSubscriptions("active");
          }
          this.loadingMembershipsTable = false;
        })
        .catch((e) => {
          this.loading = false;
          this.loadingMembershipsTable = false;
          console.log(e)
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    getExpenses() {
      this.loadingExpenses = true;
      let filters = {};
      if (this.expensesTagFilter)
        filters.tag = this.expensesTagFilter;
      if (this.fromFilter)
        filters.from = this.fromFilter;
      if (this.toFilter)
        filters.to = this.toFilter;
      Api2() /**TODO: make me not depend on stripe ID */
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/expenses",
          filters
        )
        .then((response) => {
          this.loading = false;
          this.expenses = response.data.expenses;
          this.colorExpenses();
          this.expensesFiltered = response.data.expenses;
          if (response.data.tags)
            this.expenseTags = response.data.tags;
          this.loadingExpenses = false;
        })
        .catch((e) => {
          this.loadingExpenses = false;
          console.log(e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    cancelPurchase(id) {
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/cancel/checkout/" +
          id
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.success"),
              this.$t("message.transaction_cancelled")
            );
            this.getTransactions();
          }
        });
    },
    completePurchase(id) {
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/complete/checkout/" +
          id
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.success"),
              this.$t("message.transaction_completed")
            );
            this.getTransactions();
          }
        }).catch((e) => {
          if (e)
            this.getTransactions();
        });
    },
    getMembershipTotal(t) {
      let discount = 0;
      if (t.coupon && !isNaN(t.coupon.discountedTotal) && t.coupon.discountAmount > 0) {
        discount = t.coupon.discountAmount;
      }
      const a = t.product && t.product.amountDecimal ? t.product.amountDecimal : t.amount;
      if (a > 0)
        return (a - discount).toFixed(2);
      else
        return 0;
    },
    checkRedirected() {
      if (this.code && this.state && this.scope) {
        if (this.state == this.stripe_state && this.scope == "read_write") {
          //console.log('Authenticated, send post to backend to store! ', this.code);
          Api()
            .post(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/payments/authenticate?code=" +
              this.code +
              "&state=" +
              this.state
            )
            .then((response) => {
              if (response) {
                this.showAlert("success", this.$t("message.stripe_connected"));
                //TODO reload
                this.$store.dispatch(
                  "setStripeAccountId",
                  response.data.stripeAccountId
                );
                this.stripeReady = true;
                this.getTransactions();
              }
            });
        }
      }
    },
    connectPayments() {
      const url =
        "https://connect.stripe.com/oauth/authorize?" +
        "client_id=" +
        this.stripe_client_id +
        "&state=" +
        this.stripe_state +
        "&scope=read_write&response_type=code" +
        "&stripe_user[email]=" +
        this.academy.email.toLowerCase().trim() + '&redirect_uri=https://admin.bjjlink.com/secure/transactions';
      window.location = url;
    },
    learnMore() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/learn_more"
        )
        .then((response) => {
          this.showAlert(
            "success",
            this.$t("message.thank_you"),
            this.$t("message.we_will_contact") + response.data.message
          );
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    sellItem() {
      this.navigateTo({ name: "bulksell" });
    },
    goToStripeSupport() {
      window.open('https://support.stripe.com/');
    }
  },
};
</script>

<style scoped></style>
